// src/directives/tooltipDirective.ts
import { watch } from "vue";
import type { Directive } from "vue";
import { TOOLTIP_PILL_ID, toTooltipAnchor, useTooltip, type TooltipPlacement, type TooltipVariant } from "@/stores/useTooltip";

/**
 * `v-tooltip` — the way to put a hover hint on an element you already render.
 *
 * Takes a string for the common case, or an object when the tooltip needs to be anchored to the
 * element, styled as a nav-style label, or suppressed conditionally:
 *
 *   v-tooltip="'Delete this row'"
 *   v-tooltip="{ text: 'Material Mapping', variant: 'label', placement: 'right' }"
 *   v-tooltip="{ text: label, disabled: isExpanded }"
 *   v-tooltip.truncated="name"                     // only when the text is actually clipped
 *
 * Shows on pointer enter *and* on keyboard focus, so keyboard users get the hint too, and stays silent on
 * devices without a fine pointer — a tooltip that cannot be dismissed by moving away is worse than no
 * tooltip. While shown it points the trigger's `aria-describedby` at the pill, so the hint is
 * announced rather than being decoration only.
 */

export type TooltipBindingValue =
  | string
  | {
      text: string;
      variant?: TooltipVariant;
      placement?: TooltipPlacement;
      /** Suppresses the tooltip without unbinding it, e.g. a sidebar whose label is already visible. */
      disabled?: boolean;
    };

export type NormalizedTooltipOptions = {
  text: string;
  variant: TooltipVariant;
  placement: TooltipPlacement;
  disabled: boolean;
};

/** Exported for unit tests: the string and object forms collapse to one shape. */
export function normalizeTooltipBinding(value: TooltipBindingValue | null | undefined): NormalizedTooltipOptions {
  if (value == null || typeof value === "string") {
    return { text: value ?? "", variant: "hint", placement: "cursor", disabled: false };
  }
  return {
    text: value.text ?? "",
    variant: value.variant ?? "hint",
    placement: value.placement ?? "cursor",
    disabled: value.disabled ?? false,
  };
}

/**
 * Tooltips are pointer affordances: on a touch device there is no hover to end them, so they either
 * never appear or never leave. The navigation tooltips have always gated on this; now every caller does.
 */
function canHover(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return true;
  return window.matchMedia("(hover: hover) and (pointer: fine)").matches;
}

/** Cursor placements follow the pointer and take no anchor; the rest pin to the element's rect. */
function isCursorPlacement(placement: TooltipPlacement): boolean {
  return placement === "cursor" || placement === "cursor-top";
}

// Only keyboard focus earns a persistent tooltip: a popover handing focus back to its trigger after a
// mouse click would otherwise re-show the hint and leave it until the pointer next crossed the button.
function isKeyboardFocus(el: HTMLElement): boolean {
  try {
    return el.matches(":focus-visible");
  } catch {
    // intentional: an engine without :focus-visible keeps showing on every focus, as before.
    return true;
  }
}

type ElementState = {
  options: NormalizedTooltipOptions;
  truncatedOnly: boolean;
  shown: boolean;
  /** How it was opened, so a re-show on value change keeps the same timeout behaviour. */
  shownBy: "pointer" | "focus" | null;
  /** Whatever `aria-describedby` the caller had, restored when we are done with it. */
  previousDescribedBy: string | null;
  restoreDescribedBy: (el: HTMLElement) => void;
  show: (event: Event) => void;
  hide: () => void;
  hideFromPointer: () => void;
};

// Keyed by element so nothing is stashed on the DOM node itself.
const states = new WeakMap<HTMLElement, ElementState>();

/**
 * There is one pill, so at most one trigger may point `aria-describedby` at it. Tracking that trigger
 * here covers the two ways the attribute would otherwise be left behind on an element whose tooltip is
 * gone: the store's own 5-second safety timeout hiding it without telling us, and a second trigger
 * taking the pill over (focus one thing, hover another). A dangling `aria-describedby` is worse than
 * none — it points a screen reader at an id that no longer exists.
 */
let describedTrigger: HTMLElement | null = null;
let watchingVisibility = false;

function releaseDescribedTrigger() {
  const el = describedTrigger;
  if (!el) return;
  describedTrigger = null;

  const state = states.get(el);
  state?.restoreDescribedBy(el);
  if (state) {
    state.shown = false;
    state.shownBy = null;
  }
}

function claimDescribedTrigger(el: HTMLElement, state: ElementState) {
  if (describedTrigger === el) return;
  releaseDescribedTrigger();
  state.previousDescribedBy = el.getAttribute("aria-describedby");
  el.setAttribute("aria-describedby", TOOLTIP_PILL_ID);
  describedTrigger = el;
}

/** One watcher for the whole app: the store can hide the pill on its own, and the DOM has to follow. */
function watchVisibilityOnce(visible: () => boolean) {
  if (watchingVisibility) return;
  watchingVisibility = true;
  watch(visible, (isVisible) => {
    if (!isVisible) releaseDescribedTrigger();
  });
}

const tooltipDirective: Directive<HTMLElement, TooltipBindingValue> = {
  mounted(el, binding) {
    const tooltip = useTooltip();

    const state: ElementState = {
      options: normalizeTooltipBinding(binding.value),
      truncatedOnly: !!binding.modifiers.truncated,
      shown: false,
      shownBy: null,
      previousDescribedBy: null,
      restoreDescribedBy: (target: HTMLElement) => {
        if (state.previousDescribedBy === null) target.removeAttribute("aria-describedby");
        else target.setAttribute("aria-describedby", state.previousDescribedBy);
        state.previousDescribedBy = null;
      },
      show: () => {},
      hide: () => {},
      hideFromPointer: () => {},
    };

    watchVisibilityOnce(() => tooltip.visible);

    state.show = (event: Event) => {
      const { text, variant, placement, disabled } = state.options;
      if (disabled || !text) return;
      // `.truncated`: say nothing when the text already fits, so a table only explains the cells it clipped.
      if (state.truncatedOnly && el.scrollWidth <= el.clientWidth) return;
      if (!canHover()) return;
      if (event.type === "focusin" && !isKeyboardFocus(el)) return;

      // Measured per show, not per mount: a row can scroll or reflow between the two.
      const anchorRect = isCursorPlacement(placement) ? null : toTooltipAnchor(el.getBoundingClientRect());
      // A focused element keeps its tooltip until it blurs; only pointer hovers get the safety timeout.
      const shownBy = event.type === "focusin" ? "focus" : "pointer";

      tooltip.showTooltip(text, { variant, placement, anchorRect, persistent: shownBy === "focus" });

      claimDescribedTrigger(el, state);
      state.shown = true;
      state.shownBy = shownBy;
    };

    state.hide = () => {
      if (!state.shown) return;
      releaseDescribedTrigger();
      tooltip.hideTooltip();
    };

    // An anchored pill lingers briefly on pointer-leave, so the next button in a row takes it over instead of re-mounting it.
    state.hideFromPointer = () => {
      if (!state.shown) return;
      releaseDescribedTrigger();
      tooltip.hideTooltip({ deferred: !isCursorPlacement(state.options.placement) });
    };

    states.set(el, state);

    el.addEventListener("mouseenter", state.show);
    el.addEventListener("mouseleave", state.hideFromPointer);
    el.addEventListener("focusin", state.show);
    el.addEventListener("focusout", state.hide);
  },

  // Without this the closure above keeps the binding it was mounted with, so a value that changes
  // later — a computed label, a `disabled` that follows the sidebar's expanded state — would show its
  // original text forever.
  updated(el, binding) {
    const state = states.get(el);
    if (!state) return;

    state.options = normalizeTooltipBinding(binding.value);

    if (!state.shown) return;
    // Refresh what is already on screen: hide outright if it just became disabled or empty.
    if (state.options.disabled || !state.options.text) state.hide();
    else {
      const { text, variant, placement } = state.options;
      const anchorRect = isCursorPlacement(placement) ? null : toTooltipAnchor(el.getBoundingClientRect());
      // Keep the timeout behaviour it opened with: re-showing must not quietly make a pointer
      // tooltip permanent, nor start timing out a focused one.
      useTooltip().showTooltip(text, { variant, placement, anchorRect, persistent: state.shownBy === "focus" });
    }
  },

  unmounted(el) {
    const state = states.get(el);
    if (!state) return;

    state.hide();
    el.removeEventListener("mouseenter", state.show);
    el.removeEventListener("mouseleave", state.hideFromPointer);
    el.removeEventListener("focusin", state.show);
    el.removeEventListener("focusout", state.hide);
    states.delete(el);
  },
};

export default tooltipDirective;
